<script setup lang='ts'>
import {computed, onMounted, ref, watch, onUnmounted} from 'vue';
import FlightOptions from "@/components/FlightOptions/FlightOptions.vue";
import CustomModal from "@/components/CustomModal/CustomModal.vue";
import {useRoute, useRouter} from "vue-router";
import {VueSpinner} from 'vue3-spinners';
import { useWindowSize } from '@vueuse/core';
import {Swiper, SwiperSlide} from 'swiper/vue';
import {Navigation, Pagination} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';


import ImageGallery from "@/components/ImageGallery/ImageGallery.vue";
import {useReCaptcha} from "vue-recaptcha-v3";
import {useI18n} from 'vue-i18n';
import {useUserStore} from "@/stores/userStore";
import DateInput from "@/components/Global/DateInput.vue";
import LocationPin from "@/components/Map/LocationPin.vue";
import {useYachtStore} from "@/stores/yachtStore";
import YachtBookingControl from "@/pages/YachtPage/YachtBookingControl.vue";
import FAQ from "@/components/FAQ/FAQ.vue";
import {storeToRefs} from "pinia";
import fallbackImage from '@/assets/images/boat.png';
import CustomButton from "@/components/Button/Button.vue";
import SpecialRequest from "@/components/SpecialRequest/SpecialRequest.vue";

const {t} = useI18n(); // Import the translation function
const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const yachtStore = useYachtStore();
const showOnSpotDetails = ref(false);



function closeMapPopup() {
  const mapOverlay = document.getElementById("map-overlay");
  if (mapOverlay) mapOverlay.classList.add("hidden");
}

const showFlightOptionsModal = ref(false);

const bottom = ref(500);

const showImageGalleryModal = ref(false);
const windowCurrSize = ref();
const scroll = ref(0);
const isMenuOpen = ref(false);

function windowSize() {
  windowCurrSize.value = innerWidth;
}

const threshold = computed(() => {
  if (windowCurrSize.value < 480) return 40; // Very small screens
  if (windowCurrSize.value < 768) return 50; // Mobile
  return 75; // Desktop
});
const shouldShowHeader = computed(() => yachtStore.yacht && bottom.value < threshold.value);

function onScroll() {
  scroll.value = window.scrollY;

  const element = document.querySelector('.boat-data-header-bottom');

  if (element) {
    let rect = element.getBoundingClientRect();
    bottom.value = parseInt(rect.bottom);
  }
}
const { width } = useWindowSize();
const isMobile = computed(() => width.value < 768);
const windowWidth = ref(window.innerWidth);


function updateWindowWidth() {
  windowWidth.value = window.innerWidth;
}

onMounted(() => {
  window.addEventListener("resize", updateWindowWidth);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateWindowWidth);
});


const dateInputColumns = computed(() => {
  if (windowWidth.value < 768) return 1;
  if (windowWidth.value < 1111) return 2;
  if (windowWidth.value < 1480) return 1;
  if (windowWidth.value > 1481) return 2;
  return 1; 
});

const calendarClass = computed(() => {
  if (windowWidth.value < 400) return "scale-[0.80]";
  if (windowWidth.value < 1024) return "scale-[1.00]"; 
  if (windowWidth.value < 1336) return "scale-[0.90]"; 
  if (windowWidth.value < 1920) return "scale-[1.00]";
  return "scale-[1.00]";
});

function mainImageCarousel(mainImage, altImages) {
  let result = [{id: 1999999, url: mainImage}, ...altImages];

  return result;
}

const showImagesCount = computed(() => {
  if (windowCurrSize.value <= 1024 && windowCurrSize.value > 580) {
    return 3;
  } else if (windowCurrSize.value <= 420) {
    return 1;
  } else if (windowCurrSize.value <= 580) {
    return 2;
  }

  return 3;
})
const {selections} = storeToRefs(yachtStore);

onMounted(() => {
  yachtStore.handleRoute(route)
  watch(selections, () => {
    yachtStore.fetch()
  },{deep:true})
  windowSize();
  window.addEventListener("scroll", onScroll, true)
  window.addEventListener("resize", windowSize);
});

watch(
    () => [yachtStore.selections.dateFrom, yachtStore.selections.dateTo, yachtStore.selections.persons],
    ([newDateFrom, newDateTo, newPassengers]) => {
      console.log("Yacht Page selections changed:", newDateFrom, newDateTo, newPassengers);
      yachtStore.updatePageUrl();
    },
    { deep: true }
);


function handleImageError(event: Event) {
  const target = event.target as HTMLImageElement;
  target.src = fallbackImage;
  target.onerror = null; // Prevent loop if fallback fails
}

const getAmenityLabel = (amenity) => {
  const uom = amenity.amenityUnit?.uomName;

  if (uom) {
    return uom.toLowerCase().startsWith('per ') ? uom : `Per ${uom}`;
  }

  return amenity.name;
};


const descriptionMaxLength = 450;
const showFullDescription = ref(false);

const isDescriptionLong = computed(() => {
  return yachtStore.yacht.description.length > descriptionMaxLength;
});

const truncatedDescription = computed(() => {
  return showFullDescription.value
      ? yachtStore.yacht.description
      : yachtStore.yacht.description.slice(0, descriptionMaxLength) + "...";
});

const toggleDescription = () => {
  showFullDescription.value = !showFullDescription.value;
};

const chevronIcon = computed(() => {
  return isMenuOpen.value
      ? new URL('@/assets/icons/svg/Up_16x16.svg', import.meta.url).href
      : new URL('@/assets/icons/svg/Down_16x16.svg', import.meta.url).href;
});
</script>

<template>

  <CustomModal :customIndex="1000" :show="showImageGalleryModal" style="z-index: 100 !important" :mobileClose="true"
               extraClasses="gallery-popup h-[85vh] md:h-[95vh] w-[96vw] overflow-hidden"
               @close="showImageGalleryModal = false">
    <image-gallery
        :images="mainImageCarousel(yachtStore.yacht.mainImage.url || yachtStore.yacht.mainImage.name || fallbackImage, yachtStore.yacht.images)"
        @close="showImageGalleryModal = false"/>
  </CustomModal>

  <Transition name="scroll">
    <div
        v-if="shouldShowHeader"
        v-show="yachtStore.availabilities[1]?.available"
        class="boat-data-header py-2 shadow top-18 bg-gradient-to-r from-gradients-ghostWhite to-gradients-aliceBlue fixed w-full h-fit z-10"
    >
      <div
          class="container flex flex-wrap sm:flex-nowrap justify-between items-center py-2 sm:py-4 "
      >
        <!-- Left Section -->
        <div class="boat-data-header-left-side flex flex-col flex-wrap sm:flex-nowrap sm:gap-5 justify-between">
          <div class="flex flex-col sm:flex-row sm:items-center sm:gap-5 w-full">
            <!-- Boat Name -->
            <div class="boat-name__text hidden sm:block responsive-body-large font-bold">
              {{ yachtStore.yacht.model?.name }} · {{ yachtStore.yacht.year }}
            </div>

            <!-- Date -->
            <div class="flex items-center gap-2">
              <img class="h-4 img-dp" src="@/assets/icons/svg/Calendar_16x16.svg" alt="calendar" />
              <span class="date-content responsive-body-small">
                {{ yachtStore.selectedAvailabilityDatesText }}
              </span>
            </div>

            <!-- Passengers -->
            <div class="flex items-center mt-1 sm:mt-0 gap-2">
              <img class="h-4 img-dp" src="@/assets/icons/svg/Guest_16x16.svg" alt="guests" />
              <span class="responsive-body-small">
                {{ yachtStore.selections.persons }} {{ t('passengers') }} in {{ yachtStore.yacht.cabins }} {{ t('cabins') }}
              </span>
            </div>
          </div>

          <!-- Price on Small Screens -->
          <div class="block sm:hidden price responsive-body-medium mt-1 sm:mt-2">
          <span class="responsive-title-5 font-semibold">
            {{ userStore.formatCurrency(yachtStore.priceDetails.total) }}
          </span>
          </div>
        </div>

        <!-- Right Section -->
        <div class="flex flex-col custom-flex-row items-center gap-0 sm:gap-5">
          <!-- Price on Larger Screens -->
          <div class="price-l responsive-body-medium text-right mt-2 sm:mt-0">
          <span class="responsive-title-5 font-semibold">
            {{ userStore.formatCurrency(yachtStore.priceDetails.total) }}
          </span>
          </div>

          <!-- Booking Control -->
          <div>
            <YachtBookingControl :location="yachtStore.location" />
          </div>
        </div>
        
      </div>
    </div>
  </Transition>
  

  <div class="main-yacht-page-container flex flex-col mt-28" v-if="yachtStore.yacht">

    <div class="mb-6 container">
      <CustomButton
          type="outline-black"
          :outlined="true"
          :rounded="true"
          :icon="'/assets/icons/svg/Arrow_left_16x16.svg'"
          :hoverIcon="'/assets/icons/svg/Arrow_left_white_16x16.svg'"
          @click="router.go(-1)"
      >
        {{ t('goback') }}
      </CustomButton>
    </div>
    <!-- Overview Section -->
    <section id="overview">    
    <div class="boat-data-header container border rounded-8xl">
      
<!--      Header Top-->
      <div class="boat-data-header-top min-h-[350px] py-11 gap-4 flex justify-between bg-gradient-to-r rounded-8xl from-gradients-ghostWhite to-gradients-aliceBlue">

        <div class="boat-data-header-left px-6 sm:pl-10 sm:pr-6 flex-1 flex flex-col gap-4 w-2/3">
          <!-- Top content -->
          <div class="flex flex-col gap-3">
            <LocationPin v-model="yachtStore.yachtLocation" class="location flex gap-1" />
            <div class="boat-name flex align-center gap-x-4">
              <div class="boat-name__text responsive-title-3 font-bold" v-if="yachtStore.yacht">
                {{ yachtStore.yacht.model?.name }} · {{ yachtStore.yacht.year }} | {{ yachtStore.yacht.name }}
              </div>
            </div>
          </div>

          <div class="flex flex-col sm:flex-row gap-4 mt-2">
            <!-- When Section -->
            <div class="flex flex-col sm:w-3/4">
              <div class="date-subtitle responsive-body-small flex gap-1.5 opacity-60">
                <img src="@/assets/icons/svg/Calendar_16x16.svg" alt="calendar" />
                <span>{{ t('when') }}</span>
              </div>
              <div>
                <DateInput
                    v-model:dateFrom="yachtStore.selections.dateFrom"
                    v-model:dateTo="yachtStore.selections.dateTo"
                    :show-tabs="false"
                    :columns="dateInputColumns"
                    :rows="1"
                    :calendarClass="calendarClass"
                />
              </div>
            </div>

            <!-- Passengers Section -->
            <div class="flex flex-col sm:w-1/4 sm:mt-3">
              <div class="passengers-subtitle responsive-body-small flex gap-1.5 opacity-60">
                <img src="@/assets/icons/svg/Guest_16x16.svg" alt="guests" />
                <span>{{ t('passengers') }}</span>
              </div>
              <div>
                <v-select
                    variant="underlined"
                    v-model="yachtStore.selections.persons"
                    :items="yachtStore.passengers"
                    menu-icon="null"
                    @update:menu="isMenuOpen = $event"
                >
                  <template v-slot:append-inner>
                    <img
                        :src="chevronIcon"
                        class="w-4 h-4 transition-transform duration-200"
                        alt="chevron"
                    />
                  </template>
                </v-select>
              </div>
            </div>
          </div>
        </div>
        <Transition>
          <div class="boat-data-header-right sm:py-11 px-6 sm:pr-10 sm:pl-6 flex flex-col gap-y-4 relative w-1/3">

            <!-- Loader -->
            <div v-if="yachtStore.availabilities[1]?.state === 'loading'" class="flex justify-center items-center min-h-[200px]">
              <VueSpinner size="50" class="p-2" color="#1B84FF"/>
            </div>
            <div v-else-if="yachtStore.availabilities[1]?.available">
              <div class="responsive-title-6 font-bold mb-3">{{ t('priceDetails') }}</div>
              <div class="flex flex-col gap-y-1 boat-charges">
                <div class="boat-charges responsive-body-small flex flex-col gap-y-1.5">
                  <div class="flex items-center gap-2 font-bold">
                    <div class="rounded-3xl px-3 py-1 bg-bw-silver">{{ t('freeCancellation') }}</div>
                    <div class="rounded-3xl px-3 py-1 bg-bw-white">{{ t('priceMatch') }}</div>
                  </div>
                  <div class="flex justify-between">
                    <div class="flex items-center gap-2">
                      <!-- Boat Price and Special Offer -->
                      <div class="flex gap-x-2 stack-column">
                        <div class="text-bw-darkGrey responsive-body-small ">
                          {{ t('boatPrice', { days: yachtStore.dateDays }) }}
                        </div>
                        <div class="text-primary-blue responsive-body-small" v-if="yachtStore.priceDetails.hasOffer">
                          {{ t('specialOffer') }}
                        </div>
                      </div>

                    <!-- Offer Price outside -->
                    <div
                        v-if="yachtStore.availabilities[1]?.discount > 0"
                        class="responsive-body-small bg-primary-blue text-white w-fit h-fit py-px px-2 rounded-15xl"
                    >
                      -{{ Math.round(yachtStore.availabilities[1]?.discount) }}%
                    </div>
                  </div>
                  
                  <div class="flex gap-2">
                  <span class="responsive-body-large line-through text-bw-darkGrey align-center flex" v-if="yachtStore.priceDetails.hasOffer">
                      {{ userStore.formatCurrency(yachtStore.priceDetails.basePrice) }}
                  </span>
                  </div>
                </div>
              </div>
              <div class="flex justify-between" v-for="amenity in yachtStore.priceDetails.amenities.advance"
                   :key="amenity.id">
                <span class="text-bw-darkGrey max-w-20vw">{{ amenity.name }}</span>
                <span>{{
                    amenity.subtotal > 0 ? userStore.formatCurrency(amenity.subtotal) : t('noCost')
                  }}</span>
              </div>
            </div>
            <div class="flex flex-col my-2">
              <div class="flex align-center justify-between">
                <span class="responsive-body-large font-semibold">{{ t('totalPrice') }}</span>
                <span
                    class="final-price responsive-title-4 font-bold">
                   {{ userStore.formatCurrency(yachtStore.priceDetails.total) }}
                </span>
              </div>
            </div>

              <div class="flex align-center justify-between" v-if="yachtStore.yacht.deposit">
                <span class=" responsive-body-small text-bw-darkGrey">
                {{ t('securityDeposit') }}
                </span>
                <span class="responsive-body-medium">
                    {{ userStore.formatCurrency(yachtStore.yacht.deposit) }}
                </span>
              </div>

          <div class="flex flex-col mb-2 boat-charges " v-if="yachtStore.priceDetails?.totalOnSpot">
            <div class="flex align-center justify-between color-gray">
              <div class="responsive-body-small text-bw-darkGrey">
                {{ t('yacht.price.payableOnSpot') }}
                <span @click="showOnSpotDetails = !showOnSpotDetails"
                    class=" responsive-body-small
                            cursor-pointer
                            text-black
                            transition-opacity
                            hover:opacity-60
                            text-end ml-2 my-5">
                  {{ (showOnSpotDetails ? "-" : "+") + t('yacht.price.showOnSpotDetails') }}
              </span>
                <br/>
                <span class="responsive-body-small text-bw-darkGrey">({{ t('yacht.price.payableOnSpotDetails') }})</span>
              </div>
              <span
                  class=" responsive-body-medium ">
                 {{ userStore.formatCurrency(yachtStore.priceDetails.totalOnSpot) }}
              </span>
            </div>
            <div v-if="showOnSpotDetails">
              <div class="flex justify-between py-1 responsive-body-small" v-for="amenity in yachtStore.priceDetails.amenities.separate"
                   :key="amenity.id">
                <span class="max-w-30vw responsive-body-small text-bw-darkGrey">{{ amenity.name }}</span>
                <span class="responsive-body-medium">{{
                    amenity.subtotal > 0 ? userStore.formatCurrency(amenity.subtotal) : t('noCost')
                  }}</span>
              </div>
            </div>
          </div>
              <div class="mt-3"><YachtBookingControl/></div>

        </div>

            <!--No Results Image -->
            <div v-else class="container flex flex-col justify-center items-center py-10 gap-4">
              <img src="/assets/images/search-results.svg" alt="No Results" class="2xl:w-15vw xl:w-17vw md:w-25vw sm:w-35vw">
              <div class="font-bold responsive-title-6 text-center">
                {{ t('search.couldntFindDataDescTitle') }}
              </div>
              <div class="text-center">{{ t('search.couldntFindDataDesc') }}</div>
            </div>

          </div>
        </Transition>
      </div>
          <!-- Header Bottom-->
      <div class="boat-data-header-bottom align-center pt-11 pb-2.5 px-10 flex flex-col md:flex-row justify-between">
        <div class="availability-content flex gap-2 lg:gap-6 items-center w-4/5">
          <div>
            <div class="availability-text font-bold responsive-body-large">
              {{ t('availability') }}
            </div>
          </div>

          <div class="availability-content__date flex-wrap flex md:flex-nowrap md:items-center gap-x-6">
            <!-- Previous Button -->
            <span
                :class="{
                         'text-gray-400 cursor-not-allowed flex flex-col justify-center items-center': !yachtStore.canMoveToPreviousPeriod,
                        'cursor-pointer flex flex-col justify-center items-center': yachtStore.canMoveToPreviousPeriod
                }"
                @click="yachtStore.moveToPreviousPeriod">
           <img src="@/assets/icons/svg/Left_24x24.svg" alt="Previous"/>
            </span>
            
            <div
                v-for="(dateAvailability, idx) in yachtStore.availabilities"
                :key="idx"
                v-show="!isMobile || idx === 1"
                :class="{
          'date cursor-pointer flex flex-col items-center justify-center responsive-body-medium min-w-[12vw] min-h-20': true,
          'border-b-2 border-custom-blue': idx === 1,
          'border-b-2 border-custom-grey text-gray-400 cursor-not-allowed': idx !== 1 && (dateAvailability?.offerPrice ?? 0) <= 0,
          'border-b-2 border-custom-grey transition-[border-color]': idx !== 1 && (dateAvailability?.offerPrice ?? 0) > 0,
        }"
                @click="yachtStore.selectAvailability(idx)"
            >
              <div class="flex flex-col">
                <div class="flex">
                  {{ yachtStore.availabilityDatesText(dateAvailability) }}
                </div>

                <!-- Spinner -->
                <div class="flex justify-center gap-1" v-show="dateAvailability?.state === 'loading'">
                  <VueSpinner size="25" class="p-2" color="#1B84FF"/>
                </div>

                <!-- Price -->
                <div class="flex justify-center gap-1" v-show="dateAvailability?.state === 'idle'">
            <span v-if="(dateAvailability?.offerPrice ?? 0) > 0">
              {{ t('from') }} 
              <span class="font-semibold">
                {{ userStore.formatCurrency(dateAvailability?.offerPrice ?? 0) }}
              </span>
            </span>
                  <span v-else>{{ t('notAvailable') }}</span>
                </div>
              </div>
            </div>

            <!-- Next Button -->
            <span
                class="cursor-pointer flex flex-col justify-center items-center"
                @click="yachtStore.moveToNextPeriod"
            >   <img src="@/assets/icons/svg/Right_24x24.svg" alt="Next" />
           </span>
          </div>
        </div>

        <!-- Special Request -->
        <div class="boat-data-header-bottom-right flex gap-x-6 align-center justify-center items-center mb-8">
          <div class="flex align-center gap-x-2">
            <div class="send-request-text responsive-body-medium">{{ t('specialRequest') }}</div>
              <SpecialRequest :t="t" />
          </div>
        </div>
      </div>
    </div>
      
      <div class="flex container boat-overview-content my-12 gap-x-12">
        <div class="boat-overview-content-left w-2/3">
          <img
              :src="yachtStore.yacht.mainImage.url || yachtStore.yacht.mainImage.name || fallbackImage"
              alt="boat"
              class="main-image rounded-8xl w-full h-[550px] object-cover cursor-pointer"
              @click="showImageGalleryModal = true"
              @error="handleImageError"
          />
        </div>
        <div class="boat-overview-content-right w-1/3 flex flex-col pl-0 sm:pl-6 py-6 gap-8 ">
          <div class="flex flex-col gap-4">
            <p class="boat-overview-right-title responsive-title-4 font-semibold">{{ t('overview') }}</p>
            <div class="flex flex-col gap-3">
              <p class="boat-overview-right-paragraph responsive-body-large">
                {{ truncatedDescription }}
              </p>

              <p
                  v-if="isDescriptionLong"
                  @click="toggleDescription"
                  class="boat-overview-right-read-more responsive-body-medium font-semibold underline cursor-pointer transition-opacity hover:opacity-60">
                {{ showFullDescription ? t('readLess') : t('readMore') }}
              </p>
            </div>
          </div>
  
          <div v-if="yachtStore.equipment.total > 0">
            <div class="flex flex-col gap-5">
              <span class="boat-overview-right-title-eq responsive-title-6 font-semibold">{{ t('equipment') }}</span>
              <div class='flex gap-1 flex-wrap'>
                <div v-for="equipment in yachtStore.equipment.show" :key="equipment.id">
                  <div class="bg-bw-lightGrey w-fit h-fit py-1 px-2 rounded-15xl">
                    <span class="boat-overview-right-pill text-sm">{{ equipment.name }} </span>
                  </div>
                </div>
                <div v-for="equipment in yachtStore.equipment.hide" :key="equipment.id"
                     v-show="yachtStore.equipmentShow">
                  <div class="bg-bw-lightGrey w-fit h-fit py-1 px-2 rounded-15xl">
                    <span class="boat-overview-right-pill text-sm">{{ equipment.name }} </span>
                  </div>
                </div>
              </div>
              <span v-if="!yachtStore.equipmentShow && yachtStore.equipment.hidden > 0"
                    @click="yachtStore.equipmentShow = !yachtStore.equipmentShow"
                    class="boat-overview-right-show-more responsive-body-medium font-semibold underline cursor-pointer transition-opacity hover:opacity-60">
              {{ t('more', {count: yachtStore.equipment.hidden}) }}
            </span>
            </div>
          </div>
        </div>
      </div>
      <hr/>
      
      
      <div class="container">
        <hr class="pb-10 sm:pb-12">
        <div class="c-inside pb-12 overflow-x-auto md:overflow-x-visible">
          <div class="grid grid-cols-9 lg:gap-8 gap-y-2.5 text-center w-max md:w-full">
            <div class="flex flex-col items-center justify-center gap-2 flex-shrink-0 ">
              <div class="h-10 flex items-center justify-center">
                <img class="w-10 h-10" src="@/assets/icons/svg/BuiltYear_40x40.svg" alt="year"/>
              </div>
              <p class="iconbox__text responsive-body-large mt-2">{{ yachtStore.yacht.year }}</p>
            </div>
            <div class="flex flex-col items-center justify-center gap-2 flex-shrink-0 ">
              <div class="h-10 flex items-center justify-center">
                <img class="w-10 h-10" src="@/assets/icons/svg/Cabin_40x40.svg" alt="cabins"/>
              </div>
              <p class="iconbox__text responsive-body-large mt-2">{{ yachtStore.yacht.cabins }} {{ t('cabins') }}</p>
            </div>
            <div class="flex flex-col items-center justify-center gap-2 flex-shrink-0 ">
              <div class="h-10 flex items-center justify-center">
                <img class="w-10 h-10" src="@/assets/icons/svg/Bed_40x40.svg" alt="berth"/>
              </div>
              <p class="iconbox__text responsive-body-large mt-2">{{ yachtStore.yacht.berths }} {{ t('berths') }}</p>
            </div>
            <div class="flex flex-col items-center justify-center gap-2 flex-shrink-0 ">
              <div class="h-10 flex items-center justify-center">
                <img class="w-10 h-10" src="@/assets/icons/svg/Length_40x40.svg" alt="length"/>
              </div>
              <p class="iconbox__text responsive-body-large mt-2">{{ userStore.formatLength(yachtStore.yacht.length) }}</p>
            </div>
            <div class="flex flex-col items-center justify-center gap-2 flex-shrink-0 ">
              <div class="h-10 flex items-center justify-center">
                <img class="w-10 h-10" src="@/assets/icons/svg/Mainsali_40x40.svg" alt="sailType"/>
              </div>
              <p class="iconbox__text responsive-body-large mt-2 text-center leading-snug">{{ yachtStore.yacht.sailType?.name }}</p>
            </div>
            <div v-if="yachtStore.yacht.draught" class="flex flex-col items-center justify-center gap-2 flex-shrink-0">
              <div class="h-10 flex items-center justify-center">
                <img class="w-10 h-10" src="@/assets/icons/svg/Draft_40x40.svg" alt="draught"/>
              </div>
              <p class="iconbox__text responsive-body-large mt-2">{{ userStore.formatLength(yachtStore.yacht.draught) }}</p>
            </div>
            <div class="flex flex-col items-center justify-center gap-2 flex-shrink-0 ">
              <div class="h-10 flex items-center justify-center">
                <img class="w-10 h-10" src="@/assets/icons/svg/fuel_40x40.svg" alt="fuelCapacity"/>
              </div>
              <p class="iconbox__text responsive-body-large mt-2">{{ yachtStore.yacht.fuelCapacity }} l</p>
            </div>
            <div class="flex flex-col items-center justify-center gap-2 flex-shrink-0 ">
              <div class="h-10 flex items-center justify-center">
                <img class="w-10 h-10" src="@/assets/icons/svg/Water_tank_40x40.svg" alt="waterCapacity"/>
              </div>
              <p class="iconbox__text responsive-body-large mt-2">{{ userStore.formatWeight(yachtStore.yacht.waterCapacity) }}</p>
            </div>
            <div class="flex flex-col items-center justify-center gap-2 flex-shrink-0 ">
              <div class="h-10 flex items-center justify-center">
                <img class="w-10 h-10" src="@/assets/icons/svg/Engine_40x40.svg" alt="engine"/>
              </div>
              <p class="iconbox__text responsive-body-large mt-2 text-center leading-snug">{{ yachtStore.yacht.engine }}</p>
            </div>
          </div>
        </div>
      </div>
      
    </section>
    
    <!-- Gallery Section -->
    <section id="gallery">
      <div class="bg-primary-darkBlue">
        <div class="container py-12">
          <span class="gallery-title font-semibold responsive-title-4 text-white">{{ t('gallery') }}</span>

          <!-- Desktop -->
          <div v-if="!isMobile" class="gallery-content flex gap-6 relative py-5">
            <div v-for="(image, index) in yachtStore.yacht.images.slice(0, showImagesCount)" :key="index"
                 class="image-container flex flex-col gap-2 w-2/5">
              <img @click="showImageGalleryModal = true" :src="image.url"
                   class="cursor-pointer rounded-8xl imageCol"/>
            </div>
            <button v-if="yachtStore.yacht.images.length > showImagesCount"
                    @click="showImageGalleryModal = true"
                    class="show-gallery-photos-btn transition-all absolute bottom-8 right-2 bg-white text-black responsive-body-medium px-4 py-2 rounded-12xl font-semibold">
              {{ t('showAllPhotos', {count: yachtStore.yacht.images.length - showImagesCount}) }}
            </button>
          </div>

          <!-- Mobile Swiper -->
          <Swiper
              v-else
              class="py-5"
              :modules="[Navigation, Pagination]"
              :slides-per-view="1"
              :space-between="10"
              :loop="true"
              :navigation="true"
              :pagination="{ clickable: true }"
          >
            <SwiperSlide v-for="(image, index) in yachtStore.yacht.images" :key="index">
              <img  :src="image.url"
                   class="cursor-pointer rounded-8xl w-full imageCol"/>
            </SwiperSlide>
          </Swiper>
        </div>
      </div>
    </section>

    <!-- Addons Section -->
    <section id="addons">
      <!--Price includes-->
      <div class="container">
        <div class="addons-container mt-12 flex flex-col gap-5">
          <div class="charges-title responsive-title-4 font-semibold">{{ t('includedCharges') }}</div>
          <div class="charges-subtitle  responsive-body-large text-bw-darkGrey">{{ t('includedChargesText') }}</div>
          <div class="included-charges flex flex-col sm:grid sm:grid-cols-3 gap-5 align-center">
            <div class="box-border flex align-center w-full h-100 p-5 gap-2 md:gap-0 lg:gap-6">
              <v-checkbox
                  :model-value="true"
                  :ripple="false"
                  class="always-enabled"
                  disabled
                  base-color="#1B84FF"
              />
  
              <img src="@/assets/home/svg/taxes.svg" alt="taxes" class="w-13 h-13"/>
              <div class="flex flex-col justify-between mt-auto mb-auto">
                <div class="flex">
                  <div class="included-charges-title responsive-body-large font-semibold">{{ t('taxes') }}</div>
                </div>
              </div>
            </div>
            <div class="box-border align-center flex w-full h-100 p-5 gap-2 md:gap-0 lg:gap-6">
              <v-checkbox
                  :model-value="true"
                  :ripple="false"
                  class="always-enabled"
                  disabled
                  base-color="#1B84FF"
              />
              <img src="@/assets/home/svg/insurance.svg" alt="insurance" class="w-13 h-13"/>
              <div class="flex flex-col justify-between mt-auto mb-auto">
                <div class="flex flex-col">
                  <span class="included-charges-title responsive-body-large font-semibold">{{ t('insurance') }}</span>
                </div>
              </div>
            </div>
            <div class="box-border align-center flex w-full h-100 p-5 gap-2 md:gap-0 lg:gap-6">
              <v-checkbox
                  :model-value="true"
                  :ripple="false"
                  class="always-enabled"
                  disabled
                  base-color="#1B84FF"
              />
              <img src="@/assets/home/svg/support.svg" alt="support" class="w-13 h-13"/>
              <div class="flex flex-col justify-between mt-auto mb-auto">
                <div class="flex flex-col">
                  <span class="included-charges-title responsive-body-large font-semibold">{{ t('support') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        <!--  Mandatory extras-->
        <div v-if="yachtStore.mandatoryAmenities.length > 0"
             class="addons-container mt-12 flex flex-col gap-5">
          <div class="charges-title responsive-title-4 font-semibold">{{ t('mandatoryExtras') }}</div>
          <div class="charges-subtitle  responsive-body-large text-bw-darkGrey">{{ t('mandatoryExtrasText') }}</div>
          <div class="flex gap-5">
            <div class="mandatory-extras-grid grid grid-cols-4 gap-5 w-full">
              <div v-for="amenity in yachtStore.mandatoryAmenities" :key="amenity.id"
                   class="flex flex-col">
                <div class="flex border-2 border-custom-blue rounded-8xl px-6 py-7.5 h-100 ">
                  <div class="gap-3 w-full">
                    <div class="flex justify-between items-start">
                      <div class="flex w-7/10 gap-x-3">
                        <div class="flex items-start">
  
                          <v-checkbox
                              :model-value="true"
                              :ripple="false"
                              class="always-enabled"
                              disabled
                              base-color="#1B84FF"
                          />
  
                        </div>
                        <span
                            class="mandatory-charges-title mt-auto mb-auto responsive-body-large font-semibold line-clamp-3">
                          {{ amenity.amenityGroup?.name || amenity.name }}
                        </span>
                        <span class="mandatory-charges-subtitle  responsive-body-medium text-bw-darkGrey"
                              v-if="amenity.amenityGroup">{{
                            amenity.name
                          }}</span>
                      </div>
                      <div
                          class="flex w-3/10 items-center justify-end text-end font-semibold responsive-body-medium m-auto">
  
                        {{
                          amenity.price > 0 ? userStore.formatCurrency(amenity.price) : t('noExtraCost')
                        }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
  
          <!--        Optional extras-->
          <div v-if="yachtStore.optionalAmenities.length > 0 "
               class="addons-container mt-12 flex flex-col gap-5" id="addons">
            <div class="charges-title responsive-title-4 font-semibold">{{ t('optionalExtras') }}</div>
            <div class="charges-subtitle  responsive-body-large text-bw-darkGrey">
              {{ t('optionalExtrasText') }}
            </div>
            <div class="thingsToKnow optional-extras-grid grid grid-cols-4 gap-5">
              <div v-for="amenity in yachtStore.optionalAmenities" :key="amenity.id"
                   class="amenity-card flex border rounded-8xl gap-x-3 px-6 pb-7.5 pt-10">
                <div class="flex items-start">
  
                  <v-checkbox
                      v-model="yachtStore.selectedAmenities"
                      :value="amenity.id"
                      base-color="#1B84FF"
                  />
                </div>
                <div class="flex flex-col w-full">
                  <div class="flex justify-between items-start">
                    <div class="flex flex-col w-7/10">
                    <span class="optional-charges-title responsive-body-large font-semibold">
                        {{ amenity.amenityGroup?.name || amenity.name }}
                    </span>
                    <span class="optional-charges-subtitle  responsive-body-medium text-bw-darkGrey">
                         {{ getAmenityLabel(amenity) }}
                    </span>
                    <span class="optional-charges-subtitle  responsive-body-medium text-bw-darkGrey">
                      {{ t('yacht.amenities.onSpot') }}
                    </span>
  
  
                    </div>
                    <div class="responsive-body-medium font-semibold text-end w-3/10">
                      {{
                        amenity.price > 0 ? userStore.formatCurrency(amenity.price) : t('noExtraCost')
                      }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <!-- Things to Know Section -->
    <section id="things-to">
      <FAQ />
    </section>
    <CustomModal :show="showFlightOptionsModal" @close="showFlightOptionsModal = false">
      <FlightOptions @close="showFlightOptionsModal = false"/>
    </CustomModal>

    <div id="map-overlay" class="map-popup-overlay hidden" @click="closeMapPopup">
      <div id="map-overlay-map" class="map-popup-map" @click.stop>
        <!-- Close Button -->
        <div
            @click="closeMapPopup"
            class="closemap-button cursor-pointer flex items-center justify-center z-50 absolute border border-black rounded-full p-1.5"
        >
          <img src="@/assets/icons/svg/Close_16x16.svg" alt="">
        </div>
      </div>
    </div>
    
  </div>
</template>

<style scoped>
:deep(.always-enabled .v-selection-control--disabled) {
  opacity: 1;
}

:deep(.always-enabled.v-input--horizontal) {
  grid-template-areas: unset !important;
}


.box-border {
  border-radius: 10px;
  border: 2px solid #1B84FF;
}

.chevron {
  display: inline-block;
  transition: transform 0.3s ease;
  transform: rotate(-90deg);
}

.chevron.open {
  transform: rotate(90deg);
}

.add-trip__btn:hover {
  border-color: #000 !important;
}

.amenity-card {
  display: flex;
  flex-direction: row;
  align-items: stretch;
}

.show-gallery-photos-btn:hover {
  background-color: black !important;
  color: #fff !important;
}

.imageCol {
  height: 260px;
  object-fit: cover;
}

.border-custom-blue {
  border-color: #1B84FF !important;
}

.border-custom-grey {
  border-color: #EEEEF0 !important;
}

.border-custom-grey:hover {
  border-color: #000 !important;
}

.flight-options-button:hover img {
  filter: brightness(0) invert(1);
}

.amenity-card:has(input:checked) {
  border-color: transparent !important;
  outline: solid 2px #1B84FF !important;
}

.map-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 99;
  display: flex;
  align-items: center;
  justify-content: center;
}

.map-popup-map {
  width: 80%;
  height: 80%;
  background-color: white;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
}

.closemap-button {
  position: absolute;
  top: 10px; 
  right: 10px; 
  width: 32px;
  height: 32px;
  background: white;
  border: 3px solid black;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.closemap-button img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.hidden {
  display: none;
}

@media (max-width: 1640px) {
  .boat-data-header-top .boat-data-header-left .location div {
    font-size: 0.6875rem !important;
  }

  .boat-data-header-top .boat-data-header-left .location img {
    height: 16px !important;
  }

  .boat-name .boat-name__text {
    font-size: 1.875rem;
  }

  .boat-data-header-left .date-subtitle,
  .boat-data-header-left .passengers-subtitle {
    font-size: .75rem;
  }

  .boat-data-header-left .date-content,
  .boat-data-header-left .passengers-content {
    font-size: 1.5rem;
  }

  .boat-data-header-right .header-title {
    font-size: 1.125rem;
  }

  .boat-data-header-right .boat-charges {
    font-size: 0.75rem;
  }

  .boat-data-header-right .total-text {
    font-size: .875rem;
  }

  .boat-data-header-right .final-price {
    font-size: 1.5rem;
  }

  .boat-data-header-bottom .availability-text {
    font-size: 1rem;
  }

  .boat-data-header-bottom .date,
  .boat-data-header-bottom .send-request-text,
  .boat-data-header-bottom .flight-options-button {
    font-size: .875rem;
  }

  .boat-data-header-bottom .send-request-inquiry-text {
    font-size: .75rem;
  }
}

@media (max-width: 1538px) {
  .boat-overview-right-title {
    font-size: 1.5rem;
  }

  .boat-overview-right-paragraph {
    font-size: 1rem;
  }

  .boat-overview-right-read-more {
    font-size: 0.875rem;
  }

  .boat-overview-right-title-eq {
    font-size: 1.125rem;
  }

  .boat-overview-right-pill {
    font-size: 0.75rem;
  }

  .boat-overview-right-show-more {
    font-size: 0.875rem;
  }

  .gallery-title {
    font-size: 1.5rem;
  }

  .show-gallery-photos-btn {
    font-size: .875rem;
  }

  .charges-title {
    font-size: 1.5rem;
  }

  .charges-subtitle {
    font-size: 1rem;
  }

  .included-charges-title,
  .mandatory-charges-title,
  .optional-charges-title {
    font-size: 1.125rem;
  }

  .included-charges-subtitle,
  .mandatory-charges-subtitle,
  .optional-charges-subtitle {
    font-size: .875rem;
  }

  .optional-extras-grid,
  .mandatory-extras-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .faq-title {
    font-size: 1.5rem;
  }

  :deep(.el-collapse-item .el-collapse-item__header) {
    font-size: 1.125rem;
    background: #f8f8f8;
  }
}

@media (max-width: 1450px) {
  .data-iconboxes img {
    height: 32px;
  }

  .data-iconboxes .iconbox__text {
    font-size: 1rem;
  }

  .availability-content__date {
    column-gap: 1.25rem;
  }

  .boat-data-header-bottom-right {
    width: 25%;
    
  }
}

@media (max-width: 1280px) {
  .boat-name .boat-name__text {
    font-size: 1.625rem;
  }

  .boat-data-header-left .passengers-content,
  .boat-data-header-left .date-content {
    font-size: 1.375rem;
  }

  .availability-content {
    flex-direction: column;
    justify-content: center;
    width: 100%;
    margin-bottom: 20px;
  }

  .availability-content__date {
    width: unset;
    column-gap: 1.5rem;
  }

  .boat-data-header-bottom-right {
    row-gap: 1.25rem;
    width: 45%;
  }
}

@media (max-width: 1160px) {
  .boat-overview-content {
    flex-direction: column;
  }

  .boat-overview-content .boat-overview-content-left {
    width: 100%;
  }

  .boat-overview-content .boat-overview-content-right {
    width: 100%;
  }

  .boat-overview-content-left .main-image {
    height: 400px;
  }

  .boat-overview-content-right {
    padding-right: 0;
    padding-left: 0;
  }

  .main-image {
    border-width: thin !important;
    border-style: solid !important;
    border-color: rgba(var(--v-border-color), var(--v-border-opacity));
  }
}

@media (max-width: 1110px) {
  .boat-data-header-top {
    flex-direction: column;
  }

  .boat-data-header-left, .boat-data-header-right {
    width: 100%;
  }

  .boat-data-header-left .boat-name {
    justify-content: space-between;
  }

  .data-iconboxes img {
    height: 28px;
  }
}

@media (max-width: 1055px ) {
  .boat-data-header-info{
    overflow-x: auto;
    padding-left: 0!important;
    padding-right: 0!important;
  }
  .included-charges {
    flex-direction: column;
  }

  .optional-extras-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .mandatory-extras-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 985px) {
  .extras-price {
    font-size: .875rem;
  }

  :deep(.popup-image) {
    height: 45vh !important;
  }
}

@media (max-width: 768px) {
  .price-l{
    display: none;
   }
  .img-dp{
    display: none;
  }
  .c-inside{
    padding-bottom: 15px!important;
  }
  

  .boat-data-header-info{
    overflow-x: auto;
    padding-left: 0!important;
    padding-right: 0!important;
  }

  .boat-data-header-sticky-small {
    z-index: 10;
    box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.1);
  }

  .boat-data-fixed {
    display: flex;
  }

  .boat-data-header-mobi {
    display: flex;
  }

  .boat-overview-content-right {
    padding-top: 1.25rem !important;
    padding-bottom: 1.25rem !important;
  }

  .data-iconboxes {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
  }

  .main-yacht-page-container {
    margin-top: 6.25rem
  }

  .boat-overview-content {
    margin-top: 1rem !important;
    margin-bottom: 0 !important;
  }

  .iconbox-container {
    padding-top: 1.25rem !important;
    padding-bottom: 1.25rem !important;
  }

  .boat-overview-right-title, .charges-title {
    font-size: 1.25rem;
  }

  #gallery {
    padding-top: 1.5rem !important;
    padding-bottom: 1.5rem !important;
  }

  #gallery .gallery-title {
    font-size: 1.25rem;
  }

  #gallery .gallery-content {
    padding-top: .875rem !important;
    padding-bottom: .875rem !important;
  }

  .addons-container {
    margin-top: 2.5rem !important;
    row-gap: .875rem;
  }

  #things-to {
    flex-direction: column !important;
    column-gap: 0px;
    row-gap: 1rem;
  }

  #things-to .faq-title {
    font-size: 1.25rem;
  }

  #things-to .faq-content {
    width: 100%;
  }

  :deep(.el-collapse-item .el-collapse-item__header) {
    font-size: 1rem !important;
    background: #f8f8f8;
  }

  :deep(.el-collapse-item__content) {
    font-size: .875rem !important;
  }
}

@media (max-width: 580px) {
  .image-container {
    width: 50% !important;
  }

  .boat-data-header-bottom{
    padding-left: 10px!important;
    padding-right: 20px!important;
  }

  .stack-column {
    flex-direction: column;
    gap: 4px;
  }

  .boat-data-header-bottom-right {
    row-gap: 1.25rem;
    width: 100%;
  }
  
  .v-selection-control {
    margin-top: 10px !important;
  }

  .boat-overview-right-title {
    font-size: 1.25rem;
  }

  .boat-overview-right-paragraph,
  .charges-subtitle {
    font-size: .875rem;
  }

  .boat-overview-right-read-more {
    font-size: 0.75rem;
  }

  .boat-overview-right-title-eq,
  .mandatory-charges-title,
  .optional-charges-title,
  .included-charges-title {
    font-size: 1rem;
  }

  .included-charges-subtitle,
  .mandatory-charges-subtitle,
  .optional-charges-subtitle {
    font-size: .75rem;
  }

  .boat-overview-right-pill {
    font-size: 0.6875rem;
  }

  .boat-overview-right-show-more {
    font-size: 0.75rem;
  }

  .boat-overview-content-left .main-image {
    height: 55vw;
  }

  .optional-extras-grid {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }

  .mandatory-extras-grid {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

@media (max-width: 450px) {
  .boat-data-fixed .req__btn {
    font-size: .75rem;
  }

  .map-popup-map {
    width: 95%;
    height: 95%;
    background-color: white;
    border-radius: 10px;
    position: relative;
  }

  .boat-data-fixed .date-passengers__text {
    font-size: .75rem;
  }

  .boat-data-fixed .price {
    font-size: .875rem;
  }
}

@media (max-width: 420px) {
  .image-container {
    width: 100% !important;
  }
}



/* we will explain what these classes do next! */
.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

@media (min-width: 768px) {
  .availability-content {
    margin-bottom: 30px;
  }
  
}
@media (min-width: 550px) {
  .custom-flex-row {
    flex-direction: row !important;
    gap: 1.25rem;
  }
}


@media (max-width: 499px) {
  .custom-flex-row {
    flex-direction: row !important;
    gap: 0.5rem;
  }

}

@media (max-width: 1480px) and (min-width: 1111px) {
  .stack-column {
    flex-direction: column;
    gap: 4px;
  }
}


.black-chevron .v-icon {
  color: black !important;
}

:deep(.v-field__input input) {
  font-size: 24px !important;
}

:deep(.v-select__menu-icon) {
  color: black !important;
}


:deep(.swiper-pagination) {
  text-align:start;
  margin-left: 5px;
  margin-bottom: 10px;
}
:deep(.swiper-pagination-bullet-active) {
  background: white;
}
:deep(.swiper-pagination-bullet){
  background: white;
}


:deep(.swiper-button-prev),
:deep(.swiper-button-next) {
  width: 35px ;
  height: 35px;
  background-color: white ;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.swiper-button-prev:after),
:deep(.swiper-button-next:after) {
  font-size: 16px ;
  color: black;
  font-weight: bold;
}

:deep(.swiper-button-prev) {
  left: 10px;
}

:deep(.swiper-button-next) {
  right: 10px;
}


</style>